import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import NavBar from "../components/layout/NavBar";
import {
  StepKinds,
  StepRoles,
  defaultStep,
  FlowStore,
  buildLookupSearchPayload,
} from "../components/flow/FlowTypes";
import { useAuth } from "../components/auth/AuthContext";
import { getFlows, saveFlow, deleteFlow, executeFlowSearch, getDatasourceDatabases, getDatasourceFields } from "../api/flowApi";
import { apiEndpoint } from "../utils/env";
import FlowIcon from "../components/icons/FlowIcons";
import "../components/common/common.css";

const ICON_CHOICES = [
  { key: "bank",     label: "Bank" },
  { key: "invoice",  label: "Invoice" },
  { key: "archive",  label: "Archive" },
  { key: "export",   label: "Export" },
  { key: "search",   label: "Search" },
  { key: "default",  label: "Default" },
];

function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "6px 10px", border: "1.5px solid #e2e5ef", borderRadius: 8,
          background: "#fff", cursor: "pointer", fontSize: 13, color: "#1a1a2e",
          transition: "border-color .15s",
        }}
        title="Choose icon"
      >
        <span style={{ width: 20, height: 20, display: "inline-flex" }}>
          <FlowIcon name={value || "default"} />
        </span>
        <span style={{ fontSize: 11, color: "#6b7280" }}>▾</span>
      </button>
      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 1000,
              background: "#fff", border: "1px solid #e2e5ef", borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,.12)", padding: 8,
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4,
              minWidth: 200,
            }}
          >
            {ICON_CHOICES.map((ic) => (
              <button
                key={ic.key}
                type="button"
                onClick={() => { onChange(ic.key); setOpen(false); }}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  padding: "10px 8px", border: value === ic.key ? "2px solid #3b5bdb" : "1.5px solid transparent",
                  borderRadius: 8, background: value === ic.key ? "#eef3ff" : "#f9fafb",
                  cursor: "pointer", transition: "all .12s",
                }}
              >
                <span style={{ width: 28, height: 28, display: "flex" }}>
                  <FlowIcon name={ic.key} />
                </span>
                <span style={{ fontSize: 11, fontWeight: 500, color: "#1a1a2e" }}>{ic.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const KIND_LABELS = { input: "Text Input", list: "Dropdown", dateRange: "Date Range", lookup: "Search & Select" };
const KIND_HINTS  = { input: "User types a value", list: "User picks from a list", dateRange: "User picks a date range", lookup: "Searches a database and shows results to pick from" };

// Predefined internal data sources — no URL entry needed for these
const PREDEFINED_SOURCES = [
  { value: "",                      label: "Fixed list — enter items manually",                           url: null },
  { value: "depository.databases",  label: "Depository — Document Databases",                            url: "/api/Flow/datasources/databases" },
  { value: "depository.fields",     label: "Depository — Searchable Fields  (uses selected database)",   url: "/api/Flow/datasources/fields/{{database}}" },
  { value: "custom",                label: "Custom API endpoint…",                                       url: null },
];

function StepEditor({ step, index, allSteps, token, onChange, onRemove, onInsertAfter }) {
  const update    = (k, v) => onChange(index, { ...step, [k]: v });
  const updateApi = (k, v) => update("api", { ...(step.api || {}), [k]: v });
  const [showCfg, setShowCfg] = React.useState(false);
  const mode = step.dataMode || "static";

  const updateStaticItems = (text) => {
    update("staticItems", text.split(/\n|,/).map((s) => s.trim()).filter(Boolean));
  };

  // Short summary shown under the label when config is collapsed
  const hint = [];
  if (step.key)         hint.push(`key: ${step.key}`);
  if (step.searchField) hint.push(`→ ${step.searchField}`);
  if (step.role)        hint.push(`role: ${step.role}`);
  if (step.kind === StepKinds.list) {
    const src = PREDEFINED_SOURCES.find((s) => s.value === (step.source || ""));
    if (src && src.value && src.value !== "custom") hint.push(src.label.split(" — ")[0] + " source");
    else if (step.source === "custom" && step.api?.url) hint.push(`custom: ${step.api.url.replace(/https?:\/\/[^/]+/, "")}`);
    else if (step.staticItems?.length) hint.push(`${step.staticItems.length} items`);
  }
  if (step.kind === StepKinds.lookup) {
    const dbs = step.searchConfig?.databases || [];
    if (dbs.length) hint.push(`db: ${dbs.join(", ")}`);
    if (step.selectField) hint.push(`select: ${step.selectField}`);
    const fmLen = step.searchConfig?.fieldMappings?.length || 0;
    if (fmLen) hint.push(`${fmLen} field mapping(s)`);
  }

  return (
    <div style={styles.step}>
      {/* ── Main row: type + label + actions ── */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select
          value={step.kind}
          onChange={(e) => update("kind", e.target.value)}
          style={{ ...styles.select, minWidth: 116, fontWeight: 600 }}
        >
          {Object.values(StepKinds).map((k) => (
            <option key={k} value={k}>{KIND_LABELS[k] || k}</option>
          ))}
        </select>
        <input
          style={{ ...styles.input, flex: 1 }}
          placeholder="Label shown to user"
          value={step.label || ""}
          onChange={(e) => update("label", e.target.value)}
        />
        <button className="icon-btn" title="Configure" onClick={() => setShowCfg((s) => !s)}>
          {showCfg ? "▴" : "⚙"}
        </button>
        <button className="icon-btn" title="Add step after" onClick={() => onInsertAfter(index)}>＋</button>
        <button className="icon-btn" title="Remove" onClick={() => onRemove(index)}>✕</button>
      </div>

      {/* ── Inline hint ── */}
      {!showCfg && hint.length > 0 && (
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 4, paddingLeft: 2 }}>
          {KIND_HINTS[step.kind]} · {hint.join("  ·  ")}
        </div>
      )}

      {/* ── Config panel (⚙ expanded) ── */}
      {showCfg && (
        <div style={{ marginTop: 10, padding: 10, background: "#f9f9f9", borderRadius: 6, border: "1px dashed #bbb", display: "grid", gap: 10 }}>

          {/* Text Input */}
          {step.kind === StepKinds.input && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input style={{ ...styles.input, flex: "0 1 150px" }}
                placeholder="Context key (e.g. customerNo)"
                value={step.key || ""}
                onChange={(e) => update("key", e.target.value)}
                title="Used in {{key}} templates"
              />
              <input style={{ ...styles.input, flex: 1 }}
                placeholder="Hint text inside the field"
                value={step.placeholder || ""}
                onChange={(e) => update("placeholder", e.target.value)}
              />
              <input style={{ ...styles.input, width: 140 }}
                placeholder="Index field (search)"
                value={step.searchField || ""}
                onChange={(e) => update("searchField", e.target.value)}
                title="Maps to a database index field"
              />
            </div>
          )}

          {/* Date Range */}
          {step.kind === StepKinds.dateRange && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input style={{ ...styles.input, flex: "0 1 140px" }} placeholder="From key (e.g. fromDate)" value={step.fromKey || ""} onChange={(e) => update("fromKey", e.target.value)} />
              <input style={{ ...styles.input, flex: "0 1 140px" }} placeholder="To key (e.g. toDate)"   value={step.toKey   || ""} onChange={(e) => update("toKey",   e.target.value)} />
              <input style={{ ...styles.input, width: 140 }} placeholder="Index field (search)" value={step.searchField || ""} onChange={(e) => update("searchField", e.target.value)} />
            </div>
          )}

          {/* Dropdown (list) */}
          {step.kind === StepKinds.list && (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input style={{ ...styles.input, flex: "0 1 150px" }}
                  placeholder="Context key (e.g. database)"
                  value={step.key || ""}
                  onChange={(e) => update("key", e.target.value)}
                />
                {/* Source selector — predefined internal sources + custom */}
                <select
                  value={step.source || ""}
                  style={{ ...styles.select, flex: 1 }}
                  onChange={(e) => {
                    const val = e.target.value;
                    const src = PREDEFINED_SOURCES.find((s) => s.value === val);
                    if (!val) {
                      onChange(index, { ...step, source: "", dataMode: "static" });
                    } else if (src?.url) {
                      onChange(index, { ...step, source: val, dataMode: "api", api: { ...(step.api || {}), url: src.url, method: "GET" } });
                    } else {
                      onChange(index, { ...step, source: "custom", dataMode: "api", api: { ...(step.api || {}), url: "" } });
                    }
                  }}
                >
                  {PREDEFINED_SOURCES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* Fixed list — textarea */}
              {(!step.source || step.source === "") && (
                <textarea rows={3}
                  style={{ width: "100%", boxSizing: "border-box", fontFamily: "inherit", padding: 6, border: "1px solid #bbb", borderRadius: 4 }}
                  placeholder="Items — one per line"
                  value={(step.staticItems || []).join("\n")}
                  onChange={(e) => updateStaticItems(e.target.value)}
                />
              )}

              {/* Predefined internal — show read-only URL as confirmation */}
              {step.source && step.source !== "custom" && step.api?.url && (
                <div style={{ fontSize: 11, color: "#888", padding: "4px 6px", background: "#f0f0f0", borderRadius: 4 }}>
                  {step.api.url}
                </div>
              )}

              {/* Custom API — URL + optional params */}
              {step.source === "custom" && (
                <div style={{ display: "grid", gap: 6 }}>
                  <input style={styles.input}
                    placeholder="API URL  (e.g. https://api.example.com/items)"
                    value={step.api?.url || ""}
                    onChange={(e) => updateApi("url", e.target.value)}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <select value={step.api?.method || "GET"} onChange={(e) => updateApi("method", e.target.value)} style={{ ...styles.select, width: 90 }}>
                      <option>GET</option>
                      <option>POST</option>
                    </select>
                    <input style={{ ...styles.input, flex: 1 }}
                      placeholder="Value path in response  (e.g. data.items)"
                      value={step.api?.valuePath || ""}
                      onChange={(e) => updateApi("valuePath", e.target.value)}
                    />
                  </div>
                  <textarea rows={2}
                    style={{ width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 11, padding: 6, border: "1px solid #bbb", borderRadius: 4 }}
                    placeholder='Custom headers  (JSON)  e.g. {"X-Api-Key": "abc"}'
                    value={JSON.stringify(step.api?.headers || {}, null, 2)}
                    onChange={(e) => { try { updateApi("headers", JSON.parse(e.target.value)); } catch { /* ignore */ } }}
                  />
                </div>
              )}
            </>
          )}

          {/* Lookup step config */}
          {step.kind === StepKinds.lookup && (
            <LookupStepConfig step={step} index={index} allSteps={allSteps || []} token={token} onChange={onChange} />
          )}

          {/* Search role — shared (not for lookup) */}
          {step.kind !== StepKinds.lookup && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>Search role:</span>
            <select value={step.role || ""} onChange={(e) => update("role", e.target.value)} style={{ ...styles.select, flex: 1 }}>
              <option value="">None</option>
              <option value="database">Database</option>
              <option value="field">Field name</option>
              <option value="value">Search value</option>
            </select>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Multi-select column picker: shows selected as chips + a dropdown to add more. */
function LookupColumnPicker({ fieldList, selected, onChange, inlineSelect, emptyLabel }) {
  const remaining = fieldList.filter((f) => !selected.includes(f));
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, alignItems: "center", verticalAlign: "middle" }}>
      {selected.map((col) => (
        <span key={col} style={{
          display: "inline-flex", alignItems: "center", gap: 2,
          padding: "2px 8px", background: "#e8edff", borderRadius: 4,
          fontSize: 12, fontWeight: 600, color: "#3b5bdb",
        }}>
          {col}
          <button type="button" onClick={() => onChange(selected.filter((c) => c !== col))}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 11, padding: "0 2px", lineHeight: 1 }}>✕</button>
        </span>
      ))}
      {remaining.length > 0 && (
        <select
          style={{ ...inlineSelect, minWidth: 80, fontWeight: 400, fontSize: 12 }}
          value=""
          onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]); }}
        >
          <option value="">{selected.length ? "+ add" : (emptyLabel || "— select columns —")}</option>
          {remaining.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      )}
    </span>
  );
}

/**
 * Lookup step config — two questions only:
 *   1. Which previous step holds the DATABASE name?
 *   2. Which previous step holds the FILTER FIELD name?
 *
 * Everything else happens at runtime: the step loads fields from the selected
 * database, shows a "list by" picker and a value input, then executes the search.
 */
function LookupStepConfig({ step, index, allSteps, token, onChange }) {
  const update = (k, v) => onChange(index, { ...step, [k]: v });
  const cfg = step.searchConfig || {};
  const updateCfg = (k, v) => update("searchConfig", { ...cfg, [k]: v });

  // Previous step keys for dropdowns
  const previousStepKeys = allSteps
    .slice(0, index)
    .flatMap((s) => {
      if (s.kind === StepKinds.dateRange)
        return [{ key: s.fromKey, label: `${s.label} (from)` }, { key: s.toKey, label: `${s.label} (to)` }];
      return [{ key: s.key, label: s.label || s.key }];
    })
    .filter((x) => x.key);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {/* Line 1: Database comes from which step? */}
      <div style={cfgLine}>
        <span style={cfgWord}>Database from</span>
        <select style={inlineSel}
          value={cfg.databaseContextKey || ""}
          onChange={(e) => updateCfg("databaseContextKey", e.target.value || undefined)}>
          <option value="">— pick step —</option>
          {previousStepKeys.map((pk) => <option key={pk.key} value={pk.key}>{pk.label}</option>)}
        </select>
      </div>

      {/* Line 2: Filter-by field comes from which step? */}
      <div style={cfgLine}>
        <span style={cfgWord}>Filter field from</span>
        <select style={inlineSel}
          value={cfg.fieldContextKey || ""}
          onChange={(e) => updateCfg("fieldContextKey", e.target.value || undefined)}>
          <option value="">— pick step —</option>
          {previousStepKeys.map((pk) => <option key={pk.key} value={pk.key}>{pk.label}</option>)}
        </select>
      </div>

      <div style={{ fontSize: 11, color: "#888", paddingLeft: 2 }}>
        At runtime: user picks which index to list from the selected database, enters the search value, and gets matching records.
      </div>
    </div>
  );
}

const cfgLine = { display: "flex", alignItems: "center", gap: 8, borderLeft: "3px solid #3b5bdb", paddingLeft: 10 };
const cfgWord = { fontSize: 13, color: "#555", whiteSpace: "nowrap" };
const inlineSel = {
  padding: "4px 8px", border: "1.5px solid #e2e5ef", borderRadius: 6,
  fontSize: 13, fontWeight: 600, color: "#3b5bdb", background: "#f0f4ff",
  outline: "none", minWidth: 140, appearance: "auto", cursor: "pointer",
};

// Heuristic parser to generate steps from free text (no network calls)
function parseFlowText(text) {
  if (!text) return [];
  const t = text.toLowerCase();
  const steps = [];

  const domain = guessDomain(t);

  const addInput = (label, key, placeholder) =>
    steps.push({ ...defaultStep(StepKinds.input), label, key, placeholder });
  const addList = (label, source, key) =>
    steps.push({ ...defaultStep(StepKinds.list), label, source, key });
  const addDate = (label, fromKey = "fromDate", toKey = "toDate") =>
    steps.push({ ...defaultStep(StepKinds.dateRange), label, fromKey, toKey });

  // Detect customer/policy/subscriber identifiers
  if (/customer(\s|-)?(number|id|no)/.test(t)) {
    addInput("Customer Number", "customerNumber", "Enter customer no");
  } else if (/policy (number|id)/.test(t)) {
    addInput("Policy Number", "policyNumber", "Enter policy no");
  } else if (/(msisdn|subscriber|line) (number|id)?/.test(t)) {
    addInput("Subscriber/Line", "subscriber", "Enter MSISDN or subscriber id");
  } else if (/account (number|id)/.test(t)) {
    addInput("Account Number", "accountNumber", "Enter account no");
  }

  // Detect groups layer
  if (
    /group|product (groups|types)|services/.test(t) ||
    /(credit cards|loans|accounts)/.test(t)
  ) {
    const source =
      domain === "bank"
        ? "bank.groups"
        : domain === "telco"
        ? "telco.groups"
        : "groups";
    addList("Choose group", source, "group");
  }

  // Detect specific lists
  if (/card|credit card/.test(t)) {
    addList("Choose card", "bank.cards", "card");
  }
  if (/(loan|mortgage)s?/.test(t)) {
    addList("Choose loan", "bank.loans", "loan");
  }
  if (/account(s)?/.test(t) && !/account (number|id)/.test(t)) {
    addList("Choose account", "bank.accounts", "account");
  }
  if (/policy/.test(t)) {
    addList("Choose policy", "insurance.policies", "policy");
  }

  // Date/statement detection
  if (
    /date range|between dates|from.*to|statement date|billing period|period/.test(
      t
    )
  ) {
    addDate("Date Range");
  }

  // Fallback if nothing detected: split by connectors
  if (!steps.length) {
    const parts = t
      .split(/\n|->|→|then|after that|next|\./gi)
      .map((s) => s.trim())
      .filter(Boolean);
    for (const p of parts) {
      if (/enter|type|input/.test(p)) {
        addInput("Input", camelKey(p), "Enter value");
      } else if (/select|choose|pick|list/.test(p)) {
        addList("Choose", `${domain}.list`, camelKey(p));
      } else if (/date|period|range/.test(p)) {
        addDate("Date Range");
      }
    }
  }

  // Ensure at least one step
  if (!steps.length) steps.push(defaultStep(StepKinds.input));

  return mergeSimilar(steps);
}

function guessDomain(t) {
  if (/bank|card|loan|account|statement/.test(t)) return "bank";
  if (/telco|mobile|msisdn|line|internet/.test(t)) return "telco";
  if (/policy|claim|insurance/.test(t)) return "insurance";
  return "generic";
}

function camelKey(s) {
  return s
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join("");
}

function mergeSimilar(steps) {
  const out = [];
  for (const st of steps) {
    const last = out[out.length - 1];
    if (last && last.kind === st.kind && st.kind !== StepKinds.dateRange) {
      // Prefer the more specific label/source
      out[out.length - 1] = { ...last, ...st };
    } else {
      out.push(st);
    }
  }
  return out;
}

// Normalize model-generated steps to required schema
function normalizeGeneratedSteps(steps, originalText) {
  if (!Array.isArray(steps)) return steps;
  // originalText intentionally ignored in normalization
  return steps.map((st) => {
    if (!st || typeof st !== "object") return st;
    if (st.kind === "list") {
      // If model returned an array for source, map to canonical identifier
      if (Array.isArray(st.source)) {
        // Treat as static list; preserve items
        const rawItems = st.source;
        st.staticItems = rawItems.map((x) =>
          typeof x === "string" ? x : JSON.stringify(x)
        );
        st.dataMode = st.dataMode || "static";
        // Try to infer a canonical source for semantics (optional)
        const itemsLower = rawItems.map((x) => ("" + x).toLowerCase());
        const contains = (needle) => itemsLower.some((i) => i.includes(needle));
        if (
          contains("credit") ||
          contains("loan") ||
          contains("account") ||
          contains("cards")
        ) {
          st.source =
            st.source && typeof st.source === "string"
              ? st.source
              : "bank.groups";
        } else if (contains("policy")) {
          st.source = "insurance.policies";
        } else if (!st.source || Array.isArray(st.source)) {
          st.source = "generic.list";
        }
      }
      // Heuristic: label cues
      const label = (st.label || "").toLowerCase();
      if (label.includes("group") && !/\./.test(st.source || ""))
        st.source = "bank.groups";
      if (label.includes("card") && !/\./.test(st.source || ""))
        st.source = "bank.cards";
      if (label.includes("loan") && !/\./.test(st.source || ""))
        st.source = "bank.loans";
      if (label.includes("account") && !/\./.test(st.source || ""))
        st.source = "bank.accounts";
      if (label.includes("policy") && !/\./.test(st.source || ""))
        st.source = "insurance.policies";
      // Ensure key present
      if (!st.key) {
        st.key = (st.label || "list")
          .replace(/[^a-z0-9]+/gi, " ")
          .trim()
          .split(/\s+/)
          .map((w, i) => (i ? w.charAt(0).toUpperCase() + w.slice(1) : w))
          .join("");
      }
    }
    if (st.kind === "input") {
      // Ensure placeholder defaults
      if (!st.placeholder) st.placeholder = "Enter value";
      if (!st.key)
        st.key = (st.label || "input")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_");
    }
    if (st.kind === "dateRange") {
      if (!st.fromKey) st.fromKey = "fromDate";
      if (!st.toKey) st.toKey = "toDate";
      if (!st.label) st.label = "Date Range";
    }
    if (st.kind === "lookup") {
      if (!st.key) st.key = (st.label || "lookup").replace(/[^a-z0-9]+/gi, " ").trim().split(/\s+/).map((w, i) => (i ? w.charAt(0).toUpperCase() + w.slice(1) : w.toLowerCase())).join("");
      if (!st.searchConfig) st.searchConfig = { databases: [], fieldMappings: [] };
      if (!st.columns) st.columns = [];
      if (!st.contextMappings) st.contextMappings = [];
      if (!st.selectField) st.selectField = "";
    }
    return st;
  });
}

// Functional Preview that mimics Flow Runner behavior
/** Check if a lookup step has all required context values to auto-execute.
 *  Steps using fieldContextKey always need interactive user input — never auto-execute. */
function lookupHasAllContext(step, ctx) {
  const cfg = step.searchConfig || {};
  if (cfg.fieldContextKey) return false;
  if (cfg.valueContextKey && !ctx[cfg.valueContextKey]) return false;
  for (const fm of (cfg.fieldMappings || [])) {
    if (fm.contextKey && !ctx[fm.contextKey]) return false;
  }
  return true;
}

function FunctionalPreview({ steps, token }) {
  const { t } = useTranslation();
  // Minimal mock fetchers to simulate backend-driven lists based on context
  const mockApi = {
    "bank.groups": async (ctx) => ["Credit Cards", "Loans", "Accounts"],
    "bank.cards": async (ctx) => {
      if (!ctx.customerNumber) return [];
      return ["Visa •••• 1234", "Mastercard •••• 5678"];
    },
    "telco.groups": async () => ["Mobile Lines", "Internet"],
  };

  const [ctx, setCtx] = useState({});
  const [lists, setLists] = useState({}); // index -> list items
  const [lookups, setLookups] = useState({}); // index -> { rows, loading, error }
  const [visibleCount, setVisibleCount] = useState(steps?.length ? 1 : 0);

  // Reset when steps or token changes
  useEffect(() => {
    setCtx({});
    setLists({});
    setLookups({});
    setVisibleCount(steps?.length ? 1 : 0);
  }, [steps, token]);

  const interpolate = (template, ctx) =>
    template.replace(/{{(.*?)}}/g, (_, k) => ctx[k.trim()] ?? "");

  const valueAtPath = (obj, path) => {
    if (!path) return obj;
    return path
      .split(".")
      .reduce((o, p) => (o && typeof o === "object" ? o[p] : undefined), obj);
  };

  const loadListFor = async (index, st, currentCtx) => {
    let items = [];
    if (st.dataMode === "api" && st.api?.url) {
      try {
        const raw = interpolate(st.api.url, currentCtx);
        // Resolve relative paths to the API base (avoids hitting the React dev server)
        const url = raw.startsWith("http") ? raw : apiEndpoint(raw);
        const opts = {
          method: st.api.method || "GET",
          headers: { ...(st.api.headers || {}) },
        };
        if (token) opts.headers["Authorization"] = `Bearer ${token}`;
        if (opts.method === "POST" && !opts.headers["Content-Type"]) {
          opts.headers["Content-Type"] = "application/json";
        }
        const resp = await fetch(url, opts);
        if (resp.ok) {
          const data = await resp.json();
          const arr = valueAtPath(data, st.api.valuePath || "") || [];
          if (Array.isArray(arr))
            items = arr.map((x) =>
              typeof x === "string" ? x : JSON.stringify(x)
            );
        }
      } catch (e) {
        console.warn("[FunctionalPreview] API list load failed", e);
      }
    } else if (Array.isArray(st.staticItems)) {
      items = st.staticItems;
    } else {
      const fn = st?.source ? mockApi[st.source] : null;
      items = fn ? await fn(currentCtx) : [];
    }
    setLists((prev) => ({ ...prev, [index]: items }));
  };

  // Execute a lookup step search in preview
  const executeLookupPreview = async (index, st, currentCtx) => {
    if (!token) return;
    setLookups((prev) => ({ ...prev, [index]: { rows: [], loading: true, error: "" } }));
    try {
      const payload = buildLookupSearchPayload(st, currentCtx);
      const results = await executeFlowSearch(token, payload.context, payload.searchConfig);
      const rows = Array.isArray(results) ? results : [];
      setLookups((prev) => ({ ...prev, [index]: { rows, loading: false, error: "" } }));
    } catch (e) {
      setLookups((prev) => ({ ...prev, [index]: { rows: [], loading: false, error: e.message || "Lookup failed" } }));
    }
  };

  // Load list/lookup data when a visible step appears
  useEffect(() => {
    for (let i = 0; i < Math.min(visibleCount, steps.length); i++) {
      const st = steps[i];
      const needsLoad =
        st?.kind === StepKinds.list &&
        (st.source || (st.dataMode === "api" && st.api?.url) || Array.isArray(st.staticItems));
      if (needsLoad && !lists[i]) {
        loadListFor(i, st, ctx);
      }
      if (st?.kind === StepKinds.lookup && !lookups[i] && lookupHasAllContext(st, ctx)) {
        executeLookupPreview(i, st, ctx);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, steps, ctx, token]);

  const revealNext = (fromIndex) => {
    const nextIndex = fromIndex + 1;
    if (nextIndex >= steps.length) return;
    // Clear any stale data for the next step so useEffect reloads with correct ctx
    setLists((prev) => { const n = { ...prev }; delete n[nextIndex]; return n; });
    setLookups((prev) => { const n = { ...prev }; delete n[nextIndex]; return n; });
    setVisibleCount((c) => Math.max(c, nextIndex + 1));
    // useEffect handles loading with the committed (correct) ctx
  };

  const setCtxValAt = (index, key, value) => {
    setCtx((c) => ({ ...c, [key]: value }));
    if (index < visibleCount - 1) {
      // Trim subsequent state when earlier step changes
      setLists((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          const i = Number(k);
          if (i <= index) next[i] = prev[i];
        });
        return next;
      });
      setLookups((prev) => {
        const next = {};
        Object.keys(prev).forEach((k) => {
          const i = Number(k);
          if (i <= index) next[i] = prev[i];
        });
        return next;
      });
      setVisibleCount(index + 1);
    }
  };

  const reset = () => {
    setCtx({});
    setLists({});
    setLookups({});
    setVisibleCount(steps?.length ? 1 : 0);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h3 style={{ margin: "0 0 10px" }}>
          {t("flowDesigner.functionalPreview")}
        </h3>
        <button
          className="icon-btn"
          onClick={reset}
          title={t("flowDesigner.reset")}
        >
          ↺
        </button>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {steps.slice(0, visibleCount).map((step, i) => (
          step.kind === StepKinds.lookup ? (
            <LookupStepPreview
              key={i}
              step={step}
              index={i}
              ctx={ctx}
              token={token}
              lookupData={lookups[i] || { rows: [], loading: false, error: "" }}
              onExecute={(injectedCtx, overrideSearchConfig) => {
                setCtx(injectedCtx);
                // Execute with the runtime-built searchConfig
                const st = { ...step, searchConfig: overrideSearchConfig || step.searchConfig };
                executeLookupPreview(i, st, injectedCtx);
              }}
              onSelect={(selectedCtx) => {
                setCtx((c) => ({ ...c, ...selectedCtx }));
                if (i < visibleCount - 1) {
                  setLists((prev) => { const next = {}; Object.keys(prev).forEach((k) => { if (Number(k) <= i) next[Number(k)] = prev[k]; }); return next; });
                  setLookups((prev) => { const next = {}; Object.keys(prev).forEach((k) => { if (Number(k) <= i) next[Number(k)] = prev[k]; }); return next; });
                  setVisibleCount(i + 1);
                  setTimeout(() => revealNext(i), 80);
                }
                // If last step — no further action needed, selection is the result
              }}
              onRetry={() => executeLookupPreview(i, step, ctx)}
              // onRetry is a no-op here; user just re-submits the form
            />
          ) : (
            <StepPreview
              key={i}
              step={step}
              index={i}
              ctx={ctx}
              listData={lists[i] || []}
              setCtxVal={(k, v) => setCtxValAt(i, k, v)}
              onEnter={() => revealNext(i)}
            />
          )
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9ca3af", marginBottom: 6 }}>Context</div>
        <pre style={styles.pre}>{JSON.stringify(ctx, null, 2)}</pre>
      </div>
    </>
  );
}

const previewStepCard = {
  border: "1px solid #e2e5ef",
  borderRadius: 8,
  padding: "12px 14px",
  background: "#fff",
  transition: "border-color .15s, box-shadow .15s",
};
const previewStepCardFocused = {
  ...previewStepCard,
  borderColor: "#a5b4fc",
  boxShadow: "0 0 0 3px rgba(59,91,219,.08)",
};
const previewStepNumber = {
  width: 22, height: 22, borderRadius: "50%",
  background: "#3b5bdb", color: "#fff",
  fontSize: 11, fontWeight: 700,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0,
};
const previewStepNumberDone = {
  ...previewStepNumber,
  background: "#16a34a",
};
const previewLabel = {
  fontSize: 13, fontWeight: 600, color: "#1a1a2e",
};
const previewKey = {
  fontSize: 11, fontWeight: 600, letterSpacing: ".06em",
  textTransform: "uppercase", color: "#9ca3af", marginTop: 2,
};
const previewInput = {
  width: "100%", padding: "8px 10px",
  border: "1.5px solid #e2e5ef", borderRadius: 8,
  fontSize: 14, color: "#1a1a2e", background: "#f9fafb",
  outline: "none", fontFamily: "inherit",
  transition: "border-color .15s, box-shadow .15s",
};
const previewSelect = {
  ...previewInput,
  appearance: "auto",
};

function StepPreview({ step, index, ctx, setCtxVal, listData, onEnter }) {
  const resolve = (s) =>
    (s || "").replace(/\{\{(.*?)\}\}/g, (_, k) => ctx[k.trim()] ?? `{{${k.trim()}}}`);

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (
      step.kind === StepKinds.input &&
      (ctx[step.key] ?? "").toString().trim() !== ""
    )
      return onEnter();
    if (step.kind === StepKinds.list && ctx[step.key]) return onEnter();
    if (
      step.kind === StepKinds.dateRange &&
      ctx[step.fromKey] &&
      ctx[step.toKey]
    )
      return onEnter();
  };

  const hasValue =
    step.kind === StepKinds.dateRange
      ? !!(ctx[step.fromKey] && ctx[step.toKey])
      : !!(ctx[step.key]);

  const card = hasValue ? { ...previewStepCard, borderLeftColor: "#16a34a", borderLeftWidth: 3 } : previewStepCard;
  const numStyle = hasValue ? previewStepNumberDone : previewStepNumber;

  if (step.kind === StepKinds.input) {
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={numStyle}>{index + 1}</span>
          <div>
            <div style={previewLabel}>{resolve(step.label)}</div>
            <div style={previewKey}>{step.key}</div>
          </div>
        </div>
        <input
          style={previewInput}
          placeholder={resolve(step.placeholder) || "Enter value…"}
          value={ctx[step.key] || ""}
          onChange={(e) => setCtxVal(step.key, e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => { e.target.style.borderColor = "#a5b4fc"; e.target.style.boxShadow = "0 0 0 3px rgba(59,91,219,.10)"; }}
          onBlur={(e) => { e.target.style.borderColor = "#e2e5ef"; e.target.style.boxShadow = "none"; }}
        />
      </div>
    );
  }

  if (step.kind === StepKinds.list) {
    const selected = ctx[step.key] || "";
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={numStyle}>{index + 1}</span>
          <div>
            <div style={previewLabel}>{resolve(step.label)}</div>
            <div style={previewKey}>{step.key}</div>
          </div>
        </div>
        {!listData.length ? (
          <div className="skeleton-line" style={{ width: "60%", height: 36 }} />
        ) : (
          <select
            style={previewSelect}
            value={selected}
            onChange={(e) => {
              const v = e.target.value;
              setCtxVal(step.key, v);
              if (v) onEnter();
            }}
          >
            <option value="">— select —</option>
            {listData.map((x, i) => (
              <option key={i} value={x}>{x}</option>
            ))}
          </select>
        )}
      </div>
    );
  }

  if (step.kind === StepKinds.dateRange) {
    return (
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={numStyle}>{index + 1}</span>
          <div>
            <div style={previewLabel}>{resolve(step.label)}</div>
            <div style={previewKey}>{step.fromKey} / {step.toKey}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="date"
            style={{ ...previewInput, flex: 1 }}
            value={ctx[step.fromKey] || ""}
            onChange={(e) => setCtxVal(step.fromKey, e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <input
            type="date"
            style={{ ...previewInput, flex: 1 }}
            value={ctx[step.toKey] || ""}
            onChange={(e) => setCtxVal(step.toKey, e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
    );
  }

  return null;
}

/** Preview component for lookup steps in the FunctionalPreview. */
/** Preview version of LookupStepView — mirrors runner behavior exactly. */
function LookupStepPreview({ step, index, ctx, lookupData, onSelect, onExecute, token }) {
  const { t } = useTranslation();
  const { rows, loading, error } = lookupData;
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [listFields, setListFields]   = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [runtimeFields, setRuntimeFields] = useState(null);
  const [searched, setSearched]       = useState(false);

  const cfg          = step.searchConfig || {};
  const hasSelection = !!(ctx[step.key]);
  const dbName       = cfg.databaseContextKey ? ctx[cfg.databaseContextKey] : (cfg.databases || [])[0] || "";
  const filterField  = cfg.fieldContextKey    ? ctx[cfg.fieldContextKey]    : "";

  const loadedDbRef = React.useRef(null);
  useEffect(() => {
    if (!token || !dbName || loadedDbRef.current === dbName) return;
    loadedDbRef.current = dbName;
    setRuntimeFields(null);
    getDatasourceFields(token, dbName)
      .then((fields) => setRuntimeFields(Array.isArray(fields) ? fields : []))
      .catch(() => setRuntimeFields([]));
  }, [token, dbName]);

  const canSearch = listFields.length > 0 && searchValue.trim() !== "";

  const handleSearch = () => {
    if (!canSearch) return;
    setSearched(true);
    const injected = { ...ctx, __lookupVal: searchValue };
    const sc = {
      ...(cfg.databaseContextKey
        ? { databaseContextKey: cfg.databaseContextKey }
        : { databases: cfg.databases || [] }),
      fieldMappings: [{ contextKey: "__lookupVal", fieldName: filterField, type: "value" }],
    };
    if (onExecute) onExecute(injected, sc);
  };

  const handleNewSearch = () => { setSearched(false); setSearchValue(""); setListFields([]); };

  const columns = listFields.length > 0 ? listFields
    : step.columns?.length > 0 ? step.columns
    : rows.length > 0
      ? Object.keys(rows[0]).filter(k => !["itemId","id","searchIndexes","SearchIndexes"].includes(k) && typeof rows[0][k] !== "object").slice(0,6)
      : [];

  const getF = (row, fld) => {
    if (row[fld] !== undefined) return row[fld];
    const idx = row.searchIndexes || row.SearchIndexes || {};
    return idx[fld] ?? "";
  };

  const handleRowSelect = (row, ri) => {
    setSelectedIdx(ri);
    const newCtx = {};
    const primary = listFields[0] || step.selectField || columns[0];
    if (primary) newCtx[step.key] = getF(row, primary);
    for (const m of (step.contextMappings || []))
      if (m.from && m.to) newCtx[m.to] = getF(row, m.from);
    for (const fld of listFields)
      if (fld !== primary) newCtx[fld] = getF(row, fld);
    onSelect(newCtx);
  };

  const card  = hasSelection ? { ...previewStepCard, borderLeftColor: "#16a34a", borderLeftWidth: 3 } : previewStepCard;
  const numSt = hasSelection ? previewStepNumberDone : previewStepNumber;
  const showForm = !hasSelection && (!searched || rows.length === 0);

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={numSt}>{index + 1}</span>
        <div style={{ flex: 1 }}>
          <div style={previewLabel}>{step.label || t("flow.lookup.title", "Lookup")}</div>
          {hasSelection && <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600, marginTop: 2 }}>✓ {ctx[step.key]}</div>}
        </div>
        {searched && rows.length > 0 && !hasSelection && (
          <button className="link-btn" onClick={handleNewSearch} style={{ fontSize: 11 }}>New search</button>
        )}
      </div>

      {showForm && (
        <div style={{ display: "grid", gap: 10 }}>

          {/* List by — multi chip picker */}
          <div>
            <div style={pvFieldLabel}>{t("flow.lookup.listBy", "List by")}</div>
            {runtimeFields === null ? (
              dbName
                ? <div className="skeleton-line" style={{ width: "100%", height: 34 }} />
                : <div style={{ fontSize: 12, color: "#aaa" }}>{t("flow.lookup.noDb", "No database selected in previous steps")}</div>
            ) : (
              <PreviewFieldChipPicker
                fields={runtimeFields}
                selected={listFields}
                onChange={setListFields}
              />
            )}
          </div>

          {filterField && (
            <div>
              <div style={pvFieldLabel}>
                {t("flow.lookup.where", "Where")} <strong>{filterField}</strong> =
              </div>
              <div style={{ position: "relative" }}>
                <input style={{ ...previewInput, paddingRight: 44 }}
                  placeholder={filterField}
                  value={searchValue}
                  onChange={(e) => { setSearchValue(e.target.value); if (searched) setSearched(false); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                  onFocus={(e) => { e.target.style.borderColor = "#a5b4fc"; e.target.style.boxShadow = "0 0 0 3px rgba(59,91,219,.10)"; }}
                  onBlur={(e)  => { e.target.style.borderColor = "#e2e5ef"; e.target.style.boxShadow = "none"; }}
                />
                <button
                  onClick={handleSearch}
                  disabled={!canSearch || loading}
                  title={t("flow.runner.search", "Search")}
                  style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: canSearch && !loading ? "#3b5bdb" : "#e2e5ef", border: "none", borderRadius: 6, width: 30, height: 26, cursor: canSearch && !loading ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s" }}
                >
                  {loading
                    ? <span style={{ width: 12, height: 12, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                    : <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="5.5" cy="5.5" r="4" stroke={canSearch ? "#fff" : "#aaa"} strokeWidth="1.5"/><line x1="8.5" y1="8.5" x2="12" y2="12" stroke={canSearch ? "#fff" : "#aaa"} strokeWidth="1.5" strokeLinecap="round"/></svg>
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {loading && <div className="skeleton-line" style={{ width: "60%", height: 20, marginTop: 8 }} />}

      {error && !loading && (
        <div style={{ fontSize: 12, color: "#c00", marginTop: 4 }}>
          {error}{" "}
          <button className="link-btn" onClick={handleNewSearch} style={{ fontSize: 11 }}>{t("flow.lookup.retry", "Retry")}</button>
        </div>
      )}

      {searched && !loading && !error && rows.length === 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
          <span style={{ fontSize: 12, color: "#888" }}>
            {t("flow.lookup.noResults", "No results found.")}
            {filterField && searchValue && <span style={{ color: "#bbb" }}> ({filterField} = "{searchValue}")</span>}
          </span>
          <button className="link-btn" onClick={handleNewSearch} style={{ fontSize: 11, whiteSpace: "nowrap" }}>
            {t("flow.lookup.newSearch", "Try again")}
          </button>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div style={{ overflowX: "auto", maxHeight: 220, overflowY: "auto" }}>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
            {rows.length} result(s) — click a row to select
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr>{columns.map((c) => (
                <th key={c} style={{ padding: "4px 6px", textAlign: "left", fontWeight: 600, borderBottom: "2px solid #e2e5ef", whiteSpace: "nowrap", position: "sticky", top: 0, background: "#fff" }}>{c}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} onClick={() => handleRowSelect(row, ri)}
                  style={{ background: selectedIdx === ri ? "#eef3ff" : ri % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer", outline: selectedIdx === ri ? "2px solid #3b5bdb" : "none" }}>
                  {columns.map((c) => (
                    <td key={c} style={{ padding: "3px 6px", borderBottom: "1px solid #f0f0f0", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {getF(row, c) ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const pvFieldLabel = { fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 };

function PreviewFieldChipPicker({ fields, selected, onChange }) {
  const remaining = fields.filter((f) => !selected.includes(f));
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      {selected.map((f) => (
        <span key={f} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 10px", background: "#eef3ff", border: "1.5px solid #a5b4fc", borderRadius: 6, fontSize: 13, fontWeight: 600, color: "#3b5bdb" }}>
          {f}
          <button type="button" onClick={() => onChange(selected.filter((x) => x !== f))}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#888", fontSize: 12, padding: "0 2px", lineHeight: 1 }}>✕</button>
        </span>
      ))}
      {remaining.length > 0 && (
        <select style={previewSelect} value=""
          onChange={(e) => { if (e.target.value) onChange([...selected, e.target.value]); }}>
          <option value="">{selected.length ? "+ add field" : "— select fields —"}</option>
          {remaining.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
      )}
    </div>
  );
}


export default function FlowDesigner() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [name, setName] = useState(
    "Bank: Customer → Group → Card → Statements"
  );
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [steps, setSteps] = useState([
    defaultStep(StepKinds.input),
    {
      ...defaultStep(StepKinds.list),
      label: "Choose group",
      source: "bank.groups",
      key: "group",
    },
    {
      ...defaultStep(StepKinds.list),
      label: "Choose card",
      source: "bank.cards",
      key: "card",
    },
    defaultStep(StepKinds.dateRange),
  ]);
  const [searchConfig, setSearchConfig] = useState(null);
  const [savedFlowsList, setSavedFlowsList] = useState(() => FlowStore.loadAll());
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const importFlowFileRef = useRef(null);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { candidates: Flow[] }

  // Load flows from server on mount
  useEffect(() => {
    if (!token) return;
    getFlows(token)
      .then((serverFlows) => {
        if (Array.isArray(serverFlows) && serverFlows.length > 0) {
          FlowStore.syncFromServer(serverFlows);
          setSavedFlowsList(serverFlows);
        }
      })
      .catch(() => {});
  }, [token]);

  // AI Assist state
  const [nlText, setNlText] = useState(
    "Bank call center: enter customer number, then choose group (credit cards, loans, accounts), then choose card, then date range."
  );
  const [generating, setGenerating] = useState(false);
  const [modelInfo, setModelInfo] = useState("");

  const addAfter = (i) =>
    setSteps((s) => {
      const copy = s.slice();
      copy.splice(i + 1, 0, defaultStep());
      return copy;
    });
  const removeAt = (i) => setSteps((s) => s.filter((_, idx) => idx !== i));
  const updateAt = (i, st) =>
    setSteps((s) => s.map((x, idx) => (idx === i ? st : x)));

  const save = async () => {
    const flow = { name, description, icon, steps, searchConfig };
    FlowStore.upsert(flow);
    setSaveMsg("");
    if (token) {
      setSaving(true);
      try {
        await saveFlow(token, flow);
        // Refresh saved list
        const updated = await getFlows(token);
        if (Array.isArray(updated)) { FlowStore.syncFromServer(updated); setSavedFlowsList(updated); }
        setSaveMsg(t("flowDesigner.saved", "Saved!"));
        setTimeout(() => setSaveMsg(""), 3000);
      } catch (e) {
        setSaveMsg(t("flowDesigner.saveError", "Save failed: ") + (e.message || ""));
      } finally {
        setSaving(false);
      }
    } else {
      setSaveMsg(t("flowDesigner.savedLocal", "Saved locally."));
      setTimeout(() => setSaveMsg(""), 2000);
    }
  };

  const handleExportFlows = (flows = savedFlowsList) => {
    if (!flows.length) return;
    const filename =
      flows.length === 1 ? `${flows[0].name}.depconfig.json` : "flows.depconfig.json";
    const json = JSON.stringify(flows.length === 1 ? flows[0] : flows, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Step 1: parse file → show import panel
  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const candidates = (Array.isArray(parsed) ? parsed : [parsed])
        .filter((f) => f?.name && Array.isArray(f?.steps));
      if (!candidates.length) {
        setSaveMsg(t("flowDesigner.importInvalidJson", "Invalid JSON file."));
        setTimeout(() => setSaveMsg(""), 4000);
        return;
      }
      setPendingImport({ candidates });
    } catch {
      setSaveMsg(t("flowDesigner.importInvalidJson", "Invalid JSON file."));
      setTimeout(() => setSaveMsg(""), 4000);
    }
  };

  // Step 2: user confirmed selection from import panel
  const handleConfirmImport = async (selectedFlows) => {
    setPendingImport(null);
    let added = 0, skipped = 0;
    for (const flow of selectedFlows) {
      FlowStore.upsert(flow);
      if (token) { try { await saveFlow(token, flow); } catch {} }
      added++;
    }
    const updated = token ? await getFlows(token).catch(() => null) : null;
    if (updated && Array.isArray(updated)) {
      FlowStore.syncFromServer(updated); setSavedFlowsList(updated);
    } else {
      setSavedFlowsList(FlowStore.loadAll());
    }
    setSaveMsg(t("flowDesigner.importResult", { added, skipped, defaultValue: `Import complete: ${added} added, ${skipped} skipped.` }));
    setTimeout(() => setSaveMsg(""), 4000);
  };

  const generateFromText = async () => {
    setGenerating(true);
    setModelInfo("");
    try {
      const sys =
        "You are a deterministic flow schema generator. Output ONLY a JSON array (no markdown, no prose)." +
        " Allowed step forms:" +
        " INPUT: {kind:'input',label,placeholder,key}" +
        " LIST (static): {kind:'list',label,key,dataMode:'static',staticItems:[...]}" +
        " LIST (api): {kind:'list',label,key,dataMode:'api',source:'bank.groups'|'bank.cards'|'bank.accounts'|'bank.loans'|'insurance.policies'|'telco.groups'}" +
        " DATERANGE: {kind:'dateRange',label,fromKey:'fromDate',toKey:'toDate'}." +
        " LOOKUP: {kind:'lookup',label,key,selectField:'ColumnName',columns:['Col1','Col2'],searchConfig:{databases:['DB'],fieldMappings:[{contextKey:'prevKey',fieldName:'Field',type:'value'}]},contextMappings:[{from:'Col',to:'ctxKey'}]}." +
        " Rules:" +
        " (1) If the description explicitly enumerates categories (e.g. credit cards, loans, accounts) produce a STATIC list with those exact strings in staticItems and DO NOT output an array in 'source'." +
        " (2) Never place an array under 'source'. 'source' is only used with dataMode:'api'." +
        " (3) Keys are camelCase. (4) Labels Title Case and concise. (5) No extra fields. (6) No duplicates. (7) Use fromDate/toDate for dateRange keys.";
      const body = {
        model: "gpt-4o-mini",
        temperature: 0.1,
        messages: [
          { role: "system", content: sys },
          { role: "user", content: nlText },
        ],
      };
      // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      // if (!apiKey) {
      //   console.warn(
      //     "[FlowDesigner] REACT_APP_OPENAI_API_KEY missing; using heuristic fallback"
      //   );
      //   const fallback = parseFlowText(nlText);
      //   if (fallback.length) {
      //     setSteps(fallback);
      //     setModelInfo("heuristic fallback (no key)");
      //   } else {
      //     alert("Add REACT_APP_OPENAI_API_KEY to .env and restart dev server.");
      //   }
      //   return;
      // }

      // Route through backend AiAssist endpoint (keeps API key server-side)
      const resp = await fetch(apiEndpoint("/api/AiAssist/generate"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ prompt: nlText }),
      });
      if (!resp.ok) throw new Error(await resp.text());
      const data = await resp.json();
      let gen = data?.steps || [];
      gen = normalizeGeneratedSteps(gen, nlText);
      if (Array.isArray(gen) && gen.length) {
        setSteps(gen);
        setModelInfo(data?.model || "ai");
        return;
      }
      const fallback = parseFlowText(nlText);
      if (fallback.length) {
        setSteps(fallback);
        setModelInfo("heuristic fallback");
        return;
      }
      throw new Error("No steps returned");
    } catch (err) {
      const fallback = parseFlowText(nlText);
      if (fallback.length) {
        setSteps(fallback);
        setModelInfo("heuristic fallback");
      } else {
        alert(
          "Couldn't generate steps. Configure OPENAI_API_KEY in depository-react-ui/.env and restart the dev server."
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <header className="top-bar" style={{ gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1a2e", flexShrink: 0 }}>
          {t("flowDesigner.title", "Flow Designer")}
        </span>
        <span style={{ width: 1, height: 24, background: "#e2e5ef", flexShrink: 0 }} />
        <div style={{ flex: 1, display: "flex", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 0" }}>
            <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9ca3af", display: "block", marginBottom: 3 }}>
              {t("flowDesigner.flowName", "Flow Name")}
            </label>
            <input
              className="search-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("flowDesigner.flowNamePlaceholder", "e.g. Index Search")}
              style={{ width: "100%", padding: "6px 10px", border: "1.5px solid #e2e5ef", borderRadius: 8, background: "#fff" }}
            />
          </div>
          <div style={{ flex: "2 1 0" }}>
            <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9ca3af", display: "block", marginBottom: 3 }}>
              {t("flowDesigner.description", "Description")}
            </label>
            <input
              className="search-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("flowDesigner.descriptionPlaceholder", "Short description (optional)")}
              style={{ width: "100%", padding: "6px 10px", border: "1.5px solid #e2e5ef", borderRadius: 8, background: "#fff" }}
            />
          </div>
          <div style={{ flex: "0 0 auto" }}>
            <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#9ca3af", display: "block", marginBottom: 3 }}>
              {t("flowDesigner.icon", "Icon")}
            </label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>
          <button className="go-btn" onClick={save} disabled={saving}
            style={{ padding: "7px 16px", border: "1.5px solid #e2e5ef", borderRadius: 8, fontWeight: 600, fontSize: 13 }}>
            {saving ? t("common.loading", "…") : t("common.save", "Save")}
          </button>
          {saveMsg && (
            <span style={{ fontSize: 12, color: saveMsg.includes("fail") || saveMsg.includes("Error") ? "#c00" : "#080" }}>
              {saveMsg}
            </span>
          )}
        </div>
      </header>

      <div className="main-layout" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <section className="center-panel">
          {/* Describe your flow panel */}
          <div
            style={{
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              background: "#fafafa",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Describe your flow</div>
                <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Write in plain language — AI will build the steps for you</div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {modelInfo && (
                  <span className="ai-chip" title="generator">
                    {modelInfo}
                  </span>
                )}
                <button
                  className="go-btn"
                  style={{ padding: "6px 14px", fontSize: 13 }}
                  onClick={generateFromText}
                  disabled={generating}
                >
                  {generating ? "Building…" : "Generate Steps"}
                </button>
              </div>
            </div>
            <textarea
              value={nlText}
              onChange={(e) => setNlText(e.target.value)}
              rows={4}
              placeholder={t("flowDesigner.describePlaceholder")}
              style={{
                width: "100%",
                padding: 10,
                border: "1.5px solid #222",
                borderRadius: 8,
                fontFamily: "inherit",
              }}
            />
            <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}></div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Flow Steps</div>
            <div style={{ fontSize: 12, color: "#888" }}>Each step is a question the agent will answer</div>
          </div>
          {steps.map((st, i) => (
            <StepEditor
              key={i}
              step={st}
              index={i}
              allSteps={steps}
              token={token}
              onChange={updateAt}
              onRemove={removeAt}
              onInsertAfter={addAfter}
            />
          ))}
          <button
            className="icon-btn"
            onClick={() => addAfter(steps.length - 1)}
          >
            ＋ {t("flowDesigner.addStep")}
          </button>
        </section>

        <aside className="detail-panel" style={{ padding: "16px 20px" }}>
          <FunctionalPreview steps={steps} token={token} />

          {/* Saved Flows header */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16 }}>
            <h4 style={{ margin: 0, flex: 1 }}>{t("flowDesigner.savedFlows")}</h4>
            {!showExportPanel && !pendingImport && (
              <>
                <button
                  type="button"
                  className="chip-btn chip-btn--icon"
                  onClick={() => setShowExportPanel(true)}
                  title={t("flowDesigner.exportFlows", "Export flows")}
                  disabled={!savedFlowsList.length}
                >
                  <FlowIconExport />
                </button>
                <button
                  type="button"
                  className="chip-btn chip-btn--icon"
                  onClick={() => importFlowFileRef.current?.click()}
                  title={t("flowDesigner.importFlows", "Import flows")}
                >
                  <FlowIconImport />
                </button>
                <input
                  ref={importFlowFileRef}
                  type="file"
                  accept=".json,.depconfig.json"
                  style={{ display: "none" }}
                  onChange={handleFileSelected}
                />
              </>
            )}
          </div>

          {/* Export selection panel */}
          {showExportPanel && (
            <ExportPanel
              flows={savedFlowsList}
              onExport={(selected) => { handleExportFlows(selected); setShowExportPanel(false); }}
              onClose={() => setShowExportPanel(false)}
            />
          )}

          {/* Import candidate panel */}
          {pendingImport && (
            <ImportPanel
              candidates={pendingImport.candidates}
              existingNames={new Set(savedFlowsList.map((f) => f.name))}
              onConfirm={handleConfirmImport}
              onCancel={() => setPendingImport(null)}
            />
          )}

          {/* Normal saved flows list + search config */}
          {!showExportPanel && !pendingImport && (
            <>
              <SavedFlows
                items={savedFlowsList}
                token={token}
                onLoad={(f) => {
                  setName(f.name);
                  setDescription(f.description || "");
                  setIcon(f.icon || "");
                  setSteps(f.steps || []);
                  setSearchConfig(f.searchConfig || null);
                }}
                onDeleted={(name) => {
                  const next = savedFlowsList.filter((x) => x.name !== name);
                  setSavedFlowsList(next);
                  FlowStore.saveAll(next);
                }}
              />
              <h4 style={{ marginTop: 16 }}>{t("flowDesigner.searchConfig", "Search Config")}</h4>
              <SearchConfigEditor config={searchConfig} onChange={setSearchConfig} />
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function FlowIconExport() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 1v8M5 6l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function FlowIconImport() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M8 9V1M5 4l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function SavedFlows({ items = [], token = null, onLoad, onDeleted }) {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(null);

  const remove = async (name) => {
    setDeleting(name);
    try { if (token) await deleteFlow(token, name); } catch {}
    FlowStore.remove(name);
    if (onDeleted) onDeleted(name);
    setDeleting(null);
  };

  if (!items.length)
    return <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>{t("flow.noFlow", "No saved flows.")}</div>;

  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0" }}>
      {items.map((x, i) => (
        <li key={x.name || i} style={{ ...styles.savedItem, opacity: deleting === x.name ? 0.5 : 1 }}>
          <span style={{ fontSize: 13, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {x.name}
          </span>
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            <button className="icon-btn plain" title={t("flowDesigner.load")} onClick={() => onLoad(x)}>⤓</button>
            <button
              className="icon-btn plain"
              title={t("common.remove")}
              onClick={() => remove(x.name)}
              disabled={deleting === x.name}
            >
              {deleting === x.name ? "…" : "✕"}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

const PANEL_STYLE = {
  marginTop: 10,
  border: "1px solid #dde3ef",
  borderRadius: 10,
  background: "#f8f9fc",
  overflow: "hidden",
};
const PANEL_HEADER = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "10px 12px",
  borderBottom: "1px solid #dde3ef",
  background: "#fff",
};
const PANEL_LIST = { listStyle: "none", padding: "6px 0", margin: 0, maxHeight: 220, overflowY: "auto" };
const PANEL_ROW = {
  display: "flex", alignItems: "center", gap: 8,
  padding: "6px 12px", cursor: "default",
  transition: "background 0.12s",
};
const PANEL_ACTIONS = {
  display: "flex", gap: 8, padding: "10px 12px",
  borderTop: "1px solid #dde3ef", background: "#fff",
  justifyContent: "flex-end",
};

function FlowSelectPanel({ title, items, renderBadge, onConfirm, onCancel, confirmLabel }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(new Set(items.map((x) => x.name)));

  const toggle = (name) =>
    setSelected((prev) => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleAll = () =>
    setSelected((prev) => prev.size === items.length ? new Set() : new Set(items.map((x) => x.name)));

  const allChecked = selected.size === items.length && items.length > 0;
  const someChecked = selected.size > 0 && !allChecked;

  return (
    <div style={PANEL_STYLE}>
      <div style={PANEL_HEADER}>
        <input
          type="checkbox"
          checked={allChecked}
          ref={(el) => { if (el) el.indeterminate = someChecked; }}
          onChange={toggleAll}
          style={{ cursor: "pointer", margin: 0 }}
        />
        <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: "#444" }}>{title}</span>
        <span style={{ fontSize: 11, color: "#999" }}>
          {selected.size}/{items.length}
        </span>
      </div>
      <ul style={PANEL_LIST}>
        {items.map((x) => (
          <li
            key={x.name}
            style={{ ...PANEL_ROW, background: selected.has(x.name) ? "#eef2ff" : "transparent" }}
            onClick={() => toggle(x.name)}
          >
            <input
              type="checkbox"
              checked={selected.has(x.name)}
              onChange={() => toggle(x.name)}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "pointer", margin: 0, flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {x.name}
            </span>
            {renderBadge && renderBadge(x)}
          </li>
        ))}
      </ul>
      <div style={PANEL_ACTIONS}>
        <button type="button" className="chip-btn" style={{ background: "#fff", color: "#555" }} onClick={onCancel}>
          {t("common.cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="chip-btn chip-btn--primary"
          onClick={() => onConfirm(items.filter((x) => selected.has(x.name)))}
          disabled={selected.size === 0}
        >
          {confirmLabel} ({selected.size})
        </button>
      </div>
    </div>
  );
}

function ExportPanel({ flows, onExport, onClose }) {
  const { t } = useTranslation();
  return (
    <FlowSelectPanel
      title={t("flowDesigner.selectToExport", "Select to export")}
      items={flows}
      onConfirm={onExport}
      onCancel={onClose}
      confirmLabel={t("flowDesigner.exportFlows", "Export")}
    />
  );
}

function ImportPanel({ candidates, existingNames, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <FlowSelectPanel
      title={t("flowDesigner.selectToImport", "Select flows to import")}
      items={candidates}
      renderBadge={(x) =>
        existingNames.has(x.name) ? (
          <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: "#fff3cd", color: "#856404", flexShrink: 0 }}>
            {t("flowDesigner.existsBadge", "exists")}
          </span>
        ) : (
          <span style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, background: "#d1fae5", color: "#065f46", flexShrink: 0 }}>
            {t("flowDesigner.newBadge", "new")}
          </span>
        )
      }
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmLabel={t("flowDesigner.importFlows", "Import")}
    />
  );
}

/** Minimal search config editor for the designer. */
function SearchConfigEditor({ config, onChange }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const cfg = config || {};

  const update = (k, v) => onChange({ ...cfg, [k]: v || undefined });

  return (
    <div style={{ fontSize: 12, border: "1px dashed #bbb", borderRadius: 6, padding: 8 }}>
      <button
        className="link-btn"
        style={{ fontSize: 12 }}
        onClick={() => setExpanded((s) => !s)}
      >
        {expanded ? "▴" : "▾"} {t("flowDesigner.searchConfigToggle", "Configure search mapping")}
      </button>
      {expanded && (
        <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ color: "#555" }}>{t("flowDesigner.fixedDatabases", "Fixed databases (comma-separated)")}</span>
            <input
              className="search-input"
              style={{ fontSize: 12 }}
              placeholder="e.g. POLICIES_DB, CREDIT_CARDS_DB"
              value={(cfg.databases || []).join(", ")}
              onChange={(e) => {
                const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                update("databases", arr.length ? arr : null);
              }}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ color: "#555" }}>{t("flowDesigner.databaseContextKey", "Database context key (dynamic DB selection)")}</span>
            <input
              className="search-input"
              style={{ fontSize: 12 }}
              placeholder="e.g. database"
              value={cfg.databaseContextKey || ""}
              onChange={(e) => update("databaseContextKey", e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ color: "#555" }}>{t("flowDesigner.fieldContextKey", "Field context key (dynamic field selection)")}</span>
            <input
              className="search-input"
              style={{ fontSize: 12 }}
              placeholder="e.g. fieldName"
              value={cfg.fieldContextKey || ""}
              onChange={(e) => update("fieldContextKey", e.target.value)}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ color: "#555" }}>{t("flowDesigner.valueContextKey", "Value context key (paired with field key)")}</span>
            <input
              className="search-input"
              style={{ fontSize: 12 }}
              placeholder="e.g. searchValue"
              value={cfg.valueContextKey || ""}
              onChange={(e) => update("valueContextKey", e.target.value)}
            />
          </label>
          <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
            {t("flowDesigner.searchConfigHint",
              "Tip: set 'Index field' on each input step for direct field mapping. Use roles for dynamic DB/field selection flows."
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  step: {
    border: "2px solid #222",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    background: "#fff",
  },
  row: { display: "flex", gap: 8, alignItems: "center", marginBottom: 6 },
  select: {
    padding: "8px 10px",
    border: "1.5px solid #222",
    borderRadius: 8,
    background: "#fff",
  },
  input: {
    padding: "8px 10px",
    border: "1.5px solid #222",
    borderRadius: 8,
    flex: 1,
  },
  pre: {
    background: "#111",
    color: "#f1f1f1",
    padding: 12,
    borderRadius: 8,
    overflow: "auto",
  },
  savedItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: 8,
    marginBottom: 8,
    background: "#fff",
  },
};
